import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import colors from 'src/styles/equalution/colors';
import Fonts from 'src/styles/equalution/fonts';
import { useAppSelector } from 'src/store/hooks';
import { ChallengePlan } from 'src/pages/types';
import { getCurrencySymbol, useViewport } from 'src/lib/utils';
import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

interface PlanCardProps {
  challengePlan: ChallengePlan;
  handleSelect: () => void;
  variant?: 'package' | 'bundle';
}

export const ChallengePlanCard: React.FC<PlanCardProps> = ({
  challengePlan,
  handleSelect,
  variant = 'package',
}) => {
  const {
    id,
    price,
    originalPrice,
    isBestValue,
    badgeText,
    descriptionLine1,
    descriptionLine2,
    renewalDescription,
  } = challengePlan;
  const { challengePackageSelectedPlan } = useAppSelector(
    state => state.signUp
  );
  const { isMobile } = useViewport();

  const [showDetails, setShowDetails] = useState(false);

  const isSelected = challengePackageSelectedPlan?.id === id;
  const isBundleVariant = variant === 'bundle';

  const PlanDetails = () => {
    if (!showDetails) return null;

    return (
      <Box
        sx={{
          marginTop: '10px',
          transition: 'all 1s ease-in-out',
        }}>
        {challengePlan?.details?.map(
          (points: { title: string; description?: string }) => {
            return (
              <Box
                sx={{ display: 'flex', marginBottom: '16px' }}
                key={points.title}>
                <CheckIcon sx={{ color: colors.SALAD, marginRight: '10px' }} />
                <Box>
                  <Typography
                    sx={{
                      fontSize: '16px',
                      color:
                        points.title === 'Personal coaching'
                          ? colors.SALAD
                          : colors.OFF_BLACK,
                      fontWeight: 600,
                      fontFamily: Fonts.FontType.regular,
                    }}>
                    {points.title}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '14px',
                      color: colors.OFF_BLACK,
                      fontFamily: Fonts.FontType.regular,
                      width: '70%',
                    }}>
                    {points.description}
                  </Typography>
                </Box>
              </Box>
            );
          }
        )}
      </Box>
    );
  };

  return (
    <Box
      onClick={handleSelect}
      sx={{
        height: '100%',
        flex: isBundleVariant ? { xs: '1 1 100%', sm: '1 1 320px' } : 1,
        width: isBundleVariant ? '100%' : isMobile ? '320px' : '460px',
        maxWidth: isBundleVariant ? { xs: '100%', md: '376px' } : undefined,
        backgroundColor: isSelected ? colors.OFF_WHITE : colors.CREAM_HALF,
        border: `2px solid ${isSelected ? colors.SALAD : colors.CREAM}`,
        borderRadius: '24px',
        padding: '24px',
        cursor: 'pointer',
        position: 'relative',
        transition: 'all 0.3s ease-in-out',
        boxSizing: 'border-box',
        minWidth: 0,
      }}>
      {isBestValue && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            borderRadius: '24px 24px 0 0',
            width: '100%',
            backgroundColor: isSelected ? colors.SALAD : colors.BLUEBERRY,
            color: 'white',
            textAlign: 'left',
            textTransform: 'uppercase',
            fontWeight: 700,
            fontSize: isBundleVariant ? '16px' : '14px',
            padding: isBundleVariant ? '8px 0px' : '6px 0px',
          }}>
          <span style={{ marginLeft: 24 }}>{badgeText ?? 'Best Value'}</span>
        </Box>
      )}

      <Box
        sx={{
          minHeight: isBundleVariant ? 112 : 80,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginTop: '40px',
          gap: 2,
        }}>
        <Typography
          sx={{
            fontSize: isBundleVariant ? (isMobile ? '26px' : '28px') : '32px',
            fontWeight: 700,
            color: isSelected ? colors.SALAD : colors.OFF_BLACK,
            maxWidth: '62%',
            lineHeight: 1.3,
          }}>
          {id}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            color: isSelected ? colors.SALAD : colors.OFF_BLACK,
          }}>
          <Typography
            sx={{
              fontSize: isBundleVariant ? (isMobile ? '34px' : '40px') : '40px',
              fontWeight: 700,
              color: 'inherit',
              lineHeight: 1,
            }}>
            {getCurrencySymbol()}
            {price}
          </Typography>
          {originalPrice ? (
            <Typography
              sx={{
                fontSize: isBundleVariant ? '28px' : '24px',
                fontWeight: 700,
                color: 'inherit',
                textDecoration: 'line-through',
                lineHeight: 1.2,
                marginTop: '6px',
              }}>
              {getCurrencySymbol()}
              {originalPrice}
            </Typography>
          ) : null}
        </Box>
      </Box>

      <Box my={2} sx={{ borderBottom: '1px solid #D9D9D9' }} />

      <Box
        sx={{
          minHeight: isBundleVariant ? 210 : isMobile ? 100 : 90,
        }}>
        <Typography
          sx={{
            fontSize: '16px',
            color: colors.OFF_BLACK,
            fontWeight: 600,
            fontFamily: Fonts.FontType.regular,
            paddingRight: isBundleVariant ? '8px' : '40px',
            marginBottom: '12px',
          }}>
          {descriptionLine1}
        </Typography>
        <Typography
          sx={{
            fontSize: '16px',
            color: colors.BLUEBERRY,
            fontFamily: Fonts.FontType.regular,
            paddingRight: isBundleVariant ? '8px' : '40px',
          }}>
          {descriptionLine2}
        </Typography>
        {isBundleVariant && renewalDescription && (
          <Typography
            sx={{
              fontSize: '14px',
              color: colors.BLUEBERRY,
              fontFamily: Fonts.FontType.regular,
              marginTop: '16px',
              lineHeight: 1.4,
              paddingRight: '8px',
            }}>
            {renewalDescription}
          </Typography>
        )}
      </Box>

      <Box
        zIndex={10}
        onClick={e => {
          e.stopPropagation(); // Prevents selecting the plan
          setShowDetails(!showDetails);
        }}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          color: colors.BLUEBERRY,
          fontFamily: Fonts.FontType.regular,
          marginTop: '8px',
        }}>
        <Typography
          sx={{
            fontSize: '14px',
            color: colors.SALAD,
            fontWeight: 600,
            margin: '10px 0px',
            fontFamily: Fonts.FontType.regular,
            lineHeight: '20px',
            textDecoration: 'underline',
          }}>
          {"What's included"}
        </Typography>
        {showDetails ? (
          <KeyboardArrowUpIcon color="success" />
        ) : (
          <KeyboardArrowDownIcon color="success" />
        )}
      </Box>
      <PlanDetails />
    </Box>
  );
};
